/**
 * @fileoverview Input/output schemas for story-beyond-suggester-v2.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Note: This agent intentionally omits chapterMetadata to avoid
 * anchoring the model to the current chapter structure. The focus
 * is on the complete plot arc and story world, not chapter-level details.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.actualLastThreeChaptersContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  suggestions: z.array(z.object({
    title: z.string().describe("A short, compelling title for this story continuation concept (e.g., 'The War That Followed', 'A New Generation')."),
    description: z.string().describe("A paragraph describing the proposed narrative direction that extends beyond the current story's conclusion, including new conflicts, character evolution, and thematic territory."),
    justification: z.string().describe('A brief explanation of why this continuation direction is compelling, what story threads it builds on, and how it extends the narrative world.'),
  })).length(3).describe('An array of exactly three distinct suggestions for continuing the story beyond its planned ending.'),
});

export type Output = z.infer<typeof OutputSchema>;
